<template>
	<div>
		<div v-if="!consentActive" class="alert alert-danger text-center">
			<strong>Inactive</strong>
		</div>
		<div class="card">
			<div class="card-header">
				<h3>
					<a href="/consents">Consents:</a> {{ consent.name }}
				</h3>
			</div>
			<div class="card-body">
				<div v-if="loading">
					<div class="d-flex justify-content-center align-items-center bg-dark opacity-50 w-100 h-100 position-absolute top-0 start-0">
					  	<div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
					  	<span class="ms-2">
					  		<h3 class="text-primary">Loading...</h3>
					  	</span>
				  	</div>
				</div>

				<div>
					<div class="d-flex justify-content-between mb-4">
						<div class="w-25">
							<input class="form-control" type="text" v-model="filter" placeholder="Search participants by STAR ID or name" @input="fetchDebounced">
						</div>
						<div>
							<button v-if="consentActive" class="btn btn-primary" :disabled="selectedParticipants.length === 0" @click="bulkAssignTasks">Assign Consent Task</button>
							<span v-if="!consentActive" class="fst-italic text-danger">Task assignment is disabled for inactive consents.</span>
						</div>
					</div>
					<table class="table">
						<thead>
							<tr>
								<th>
									<span class="sort d-flex justify-contents-center align-items-baseline" @click="applySort('star_id')">
	                                    <span>STAR ID</span>
	                                    <down-arrow v-if="sortDirection == 'asc' || sortField != 'star_id'" /> 
	                                    <up-arrow v-if="sortDirection == 'desc' && sortField == 'star_id'" />
	                                </span>
								</th>
								<th>
									<span class="sort d-flex justify-contents-center align-items-baseline" @click="applySort('first_name')">
	                                    <span>Name</span>
	                                    <down-arrow v-if="sortDirection == 'asc' || sortField != 'first_name'" /> 
	                                    <up-arrow v-if="sortDirection == 'desc' && sortField == 'first_name'" />
	                                </span>
	                            </th>
								<th>
									<span class="sort d-flex justify-contents-center align-items-baseline" @click="applySort('age')">
	                                    <span>Age</span>
	                                    <down-arrow v-if="sortDirection == 'asc' || sortField != 'age'" /> 
	                                    <up-arrow v-if="sortDirection == 'desc' && sortField == 'age'" />
	                                </span>
								</th>
								<th>
									<span class="sort d-flex justify-contents-center align-items-baseline" @click="applySort('is_spanish_speaking')">
	                                    <span>Language</span>
	                                    <down-arrow v-if="sortDirection == 'asc' || sortField != 'is_spanish_speaking'" /> 
	                                    <up-arrow v-if="sortDirection == 'desc' && sortField == 'is_spanish_speaking'" />
	                                </span>
								</th>
								<th>
									<span class="sort d-flex justify-contents-center align-items-baseline" @click="applySort('last_signed')">
	                                    <span>Last {{ consent.consent_type.slug == 'main' ? 'Main Consent' : '' }} Signed</span>
	                                    <down-arrow v-if="sortDirection == 'asc' || sortField != 'last_signed'" /> 
	                                    <up-arrow v-if="sortDirection == 'desc' && sortField == 'last_signed'" />
	                                </span>
								</th>
								<th v-if="consentActive" scope="col" class="col-2">Task</th>
								<th v-if="consentActive">
									<input type="checkbox" 
										id="select-all-checkbox"
										v-model="allChecked"
										@input="toggleAllSelectedParticipants"
									>
								</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="participant in participants" :key="participant.id">
								<td>{{ participant.star_id }}</td>
								<td><a :href="'/participants/'+participant.id">{{ participant.full_name }}</a></td>
								<td>{{ participant.age }}</td>
								<td>{{ participant.language }}</td>
								<td>{{ consent.consent_type.slug == 'main' ? participant.last_signed_name : participant.last_signed_date }}</td>
								<td v-if="participant.current_task == null && consentActive">
									<button class="btn btn-sm btn-outline-primary" 
										@click="assignSingleTask(participant.id)" 
										:disabled="selectedParticipants.length !== 0">
										Assign Consent Task
									</button>
								</td>
								<td v-if="participant.current_task != null && consentActive">
									<a :href="'/tasks/'+participant.current_task.id" class="text-wrap">{{ consent.name }}</a>
									<br />
									<button class="btn btn-sm btn-outline-secondary" 
										@click="cancelSingleTask(participant.full_name, participant.current_task.id)">
										cancel
									</button>
								</td>
								<td v-if="consentActive">
									<input type="checkbox" 
										:id="'checkbox_'+participant.id" 
										:value="participant.id"
										:disabled="participant.current_task != null"
										v-model="selectedParticipants"
									>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapState } from 'vuex'
import _ from 'lodash'
import DownArrow from '../shared/DownArrow.vue'
import UpArrow from '../shared/UpArrow.vue'

export default {
	name: "Consent Detail",
	components: {
		DownArrow,
		UpArrow
	},
	props: {
		consent: Object,
	},
	data() {
		return {
			allChecked: false,
			consentActive: this.consent.active,
			filter: null,
		}
	},
	methods: {
		applySort(field) {
			if(field == this.sortField) {
                let direction = this.sortDirection == 'asc' ? 'desc' : 'asc'
                this.$store.dispatch('consents/setSortDirection', direction)
            }
            else {
                this.$store.dispatch('consents/setSortDirection', 'asc')
                this.$store.dispatch('consents/setSortField', field)
            }
			this.$store.dispatch('consents/fetchParticipants')
		},
		assignSingleTask(part_id) {
			this.$store.commit('consents/updateSelectedParticipants', [part_id])
			this.bulkAssignTasks()
		},
		cancelSingleTask(part_name, task_id) {
			let confirmationMessage = 'You are about to cancel a consent task for '+part_name+'. Are you sure you want to continue?'
			if(confirm(confirmationMessage)) {
				this.$store.dispatch('consents/cancelTask', task_id)
			}
		},
		bulkAssignTasks() {
			this.$store.dispatch('consents/assignTask')
		},
		fetchDebounced: _.debounce(function() {
			this.$store.dispatch('consents/setFilter', this.filter)
            this.$store.dispatch('consents/fetchParticipants')
        }, 500),
        toggleAllSelectedParticipants() {
        	this.$store.dispatch('consents/toggleAllSelectedParticipants', this.allChecked)
        }
	},
	computed: {
		...mapState({
            errors: state => state.consents.errors,
            participants: state => state.consents.participants,
            sortDirection: state => state.consents.sortDirection,
            loading: state => state.consents.loading,
            sortField: state => state.consents.sortField
        }),
        selectedParticipants: {
		    get () {
		      	return this.$store.state.consents.selectedParticipants
		    },
		    set (value) {
		      	this.$store.commit('consents/updateSelectedParticipants', value)
		    }
		}
	},
	mounted() {
		this.$store.dispatch('consents/setConsent', this.consent)
		this.$store.dispatch('consents/fetchParticipants')
	}
}
</script>

<style scoped>
	.sort {
		cursor: pointer;
	}
</style>